<template>
    <b-button 
        :variant="buttonVariant"
        :href="fileHref"
        :disabled="mode !== 'loaded'"
        :block="block"
        :size="size"
        class="p-0 file-download-badge font-weight-400"
        target="_blank"
    >
        <div v-if="['notLoaded', 'loading'].includes(mode)" key="loadingSpinner" class="px-5 py-2">
            <b-spinner small></b-spinner>
        </div>
        <div v-if="mode == 'incompleteData'" key="incompleteDataMode" class="px-4 py-2">
            {{ incompleteDataText }}
        </div>
        <div v-if="mode == 'notAllowed'" key="notAllowed" class="px-4 py-2">
            No Access
        </div>
        <div 
            v-if="mode == 'loaded'" 
            key="loaded" 
            :id="`s3-file-button-content-${localId}`"
            class="h-100 d-flex align-items-center justify-content-between text-left"
        >
            <div class="h-100 flex-grow-1 d-flex align-items-center text-left">  <!-- Left side things -->
                <div 
                    :class="hideFilesize ? 'px-1' : 'px-2'"
                    class="h-100 py-1 d-flex justify-content-center align-items-center"
                >    
                    <span 
                        :title="localFileData.filename" 
                        :class="[fileIconString, hideFilesize ? 'font-size-100' : 'font-size-150']"
                    ></span>
                </div>
                <!-- text-truncate (which does not work) just has     overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap; -->
    <!-- text-break has word-break: break-word !important; word-wrap: break-word !important -->
    <!-- TO MAKE ELLIPSIS WORK, you likely have to set widths specifically... set them for file icon and download icon, then calculate middle TODO -->
                <div class="h-100 flex-grow-1 py-1 px-2 font-size-80 d-flex flex-wrap flex-row align-items-center justify-content-between" style="column-gap: 0.5rem;">
                    <div class="text-break" style="max-width: 300px;">{{ showTitle ? localFileData.title : localFileData.filename }}</div>  <!-- had class="text-break" until Sept 2024 -- truncate long filenames, but show them on hover with title=... -->
                    <!-- <div v-if="!showTitle" class="small text-muted"></div> -->  <!-- ??? wtf? seems we were never showing this, so just removing for now -->
                    <div v-if="!hideFilesize" class="small text-muted" style="max-width: 300px;">{{ localFileData.filesize | formatFileSize }}</div>
                </div>
            </div>
            <div 
                v-if="!hideDownloadIcon"
                class="h-100 py-1 px-2 d-flex justify-content-center align-items-center"
            >  <!-- Right side things -->
                <b-icon icon="download" :font-scale="hideFilesize ? '1' : '1.5'"></b-icon>
            </div>
        </div>
        <b-popover v-if="enablePreview" :target="`s3-file-button-content-${localId}`" triggers="hover" placement="left" :delay="250">
            <a :href="fileHref" target="_blank" class="w-100 h-100 min-height-100 bg-gray-100 d-block s3file-preview-content" style="min-width: calc(276px - 1.5rem);">
                <b-img v-if="isImage" key="isImage" fluid :src="fileHref"></b-img>
                <div v-else key="noImage" class="h-100 px-2 py-3 d-flex flex-column align-items-center justify-content-center">
                    <span class="font-size-300 text-decoration-none" :class="fileIconString"></span>
                    <div class="text-center font-weight-500 text-dark mt-2 text-decoration-none">{{ localFileData.filename }}</div>
                    <div class="text-center text-muted font-size-70 text-decoration-none">{{ localFileData.filesize | formatFileSize }}</div>
                </div>
            </a>
        </b-popover>
    </b-button>
</template>

<script>
import { FormatFileSizeHelper } from '../../js/filters-helpers';
import { getS3FileDownloadRedirectUrl, loadS3FileMeta } from './s3_file_meta';

export default {
    name: 'BaseS3FileDownloadButton',
    props: {
        fileData: {  // object from your standard S3File serializer (or file meta) -- you need at least: 'id', 'file_category', 'filename' and 'filesize'
            // older payloads without id/file_category fall back to linking 'download_url' directly (which expires)
            type: Object,
            required: false,
        },
        // MUST PROVIDE EITHER fileData OR category + id - if category/id, we fetch the rest (batched with all other buttons on the page)
        // either way, we link to our redirect view, which signs a fresh S3 URL on click - so links never expire
        category: {
            // the s3file category - each class has a unique one - may also be in fileData.file_category
            type: String,
            required: false,
        },
        id: {
            type: String,
            required: false,
        },
        showTitle: {  // set to true to show title in prominent position, filename below.  False means no title shown.
            type: Boolean,
            default: false,
        },
        buttonVariant: {  // should really just change this to 'variant' since it is passed to b-button directly
            type: String,
            default: 'minimal-primary',
        },
        hideFilesize: {  // if set, it hides filesize and renders a smaller file icon to make overall button smaller
            type: Boolean,
            default: false,
        },
        hideDownloadIcon: {  // if true, hide the download icon on right side
            type: Boolean,
            default: false,
        },
        block: {  // block button?
            type: Boolean,
            default: false,
        },
        size: {  // doesn't do much (anything) right now
            type: String,
            default: 'md',
        },
        showPopoverPreview: {  // set true to show a popover preview of images
            // REMEMBER:  The target element must exist in the document before <b-popover> is mounted. If the target element is not found during mount, the popover will never open.
            type: Boolean,
            default: false,
        },
        incompleteDataText: {
            // text to show if we don't have enough data to show or fetch the file
            type: String,
            default: 'Not Available',
        },
    },
    data() {
        return {
            isLoaded: false,  // set true when we know we either have fileData from prop, or did the fetch
            loadingFileData: false,  // true only while fetching from API
            localFileData: {},  // set in created() if you have fileData prop, or from API response (file meta - no download_url)
            notAllowed: false,  // if user does not have access to view file (or it was not found), we set this true
            incompleteData: false,  // if we don't have enough data to show things or fetch things, set this true and show 'incompleteDataText'
        }
    },
    computed: {
        fileHref() {
            // link to our redirect view (signs a fresh S3 URL on click) -- only use the signed download_url if we don't know category/id
            // it depends on where the data came from.... could be there is no download_url because the user has no access... but that's fine, they'll just get rejected when they do a download attempt
            if (this.localFileCategory && this.localId) {
                return getS3FileDownloadRedirectUrl(this.localFileCategory, this.localId);
            }
            return this.localFileData?.download_url || '';
        },
        hasFileData() {
            return this.isLoaded && !!this.fileHref;
        },
        localFileCategory() {
            return this.category || this.fileData?.file_category || '';
        },
        localId() {
            // use this for when we need actual unique IDs for things like popover targets
            return this.id || this.localFileData.id;
        },
        mode() {
            // centralize logic for controlling the display mode/state
            // options are 
            // - 'notLoaded' (quick flash before fetching, in normal circumstances)
            // - 'loading'  (while fetching)
            // - 'loaded'  (we have data from prop, or from API)
            // - 'incompleteData'  (not enough data to fetch or show anything... should show incompleteDataText)
            // - 'notAllowed'  (no read access, file not found, or bad response from API)
            if (this.notAllowed) {
                return 'notAllowed';
            } else if (this.hasFileData) {
                return 'loaded';
            } else if (this.loadingFileData) {
                return 'loading';
            } else if (this.incompleteData) {
                return 'incompleteData';
            } else {  // unknown if we'll have data or not...
                return 'notLoaded';
            }
        },
        enablePreview() {
            // can we preview this file - if an image - based on mimetype - localFileData.filetype
            if (!this.showPopoverPreview || (this.mode !== 'loaded')) {
                return false;
            }
            return true;
            //const mimetype = this.localFileData?.filetype ?? '';
            //return mimetype.startsWith('image');
        },
        isImage() {
            // is this an image that we could potentially preview, based on mimetype?
            const mimetype = this.localFileData?.filetype ?? '';
            return mimetype.startsWith('image');
        },
        fileIconString() {
            return `fiv-sqo fiv-icon-${this.localFileData.file_extension}`;
        },
    },
    filters: {
        formatFileSize: function(value) {
            return FormatFileSizeHelper(value);
        }
    },
    created() {
        if (this.fileData) {
            this.localFileData = {...this.fileData};
            this.isLoaded = true;
            this.notAllowed = this.fileData.can_read === false;  // file meta payloads tell us up front - full serializer payloads don't have it
        }
    },
    mounted() {
        if (this.isLoaded) {
            // had fileData from prop - nothing to fetch, but make sure we have something to link to
            this.incompleteData = !this.fileHref;
        } else if (!this.localFileCategory || !this.id) {
            // no fileData from prop, so we must have 'category' and 'id' to fetch data
            console.warn('Trying to show a file download button, but not enough data from props to fetch data from API');
            this.incompleteData = true;
        } else {
            this.getFileData();
        }
    },
    methods: {
        async getFileData() {
            this.loadingFileData = true;
            try {
                const fileMeta = await loadS3FileMeta(this.localFileCategory, this.id);  // batched with all other buttons on the page
                if (fileMeta?.can_read) {
                    this.localFileData = fileMeta;
                    this.isLoaded = true;
                } else {
                    this.notAllowed = true;  // not found, or no read access
                }
            } catch (err) {
                console.error(err);
                this.notAllowed = true;
            }
            this.loadingFileData = false;
        }
    },
}
</script>

<style lang="scss">
a.s3file-preview-content {
    text-decoration: none;
}

</style>